import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUp, ShoppingCart, BookMarked } from "lucide-react";
import { Atmosphere } from "@/components/atmosphere/Atmosphere";
import { WaitlistForm } from "@/components/WaitlistForm";
import { SiteFooter } from "@/components/SiteFooter";
import { useSecrets } from "@/context/SecretsContext";
import { useStore } from "@/context/StoreContext";
import { StorePicker } from "@/components/StorePicker";
import { EXCERPT } from "@/data/excerpt";
import { BOOKS, SITE } from "@/data/series";
import { useSeo, SITE_ORIGIN } from "@/hooks/use-seo";
import { recordReadProgress } from "@/lib/api";

const READ_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "The Fractured Five",
          item: `${SITE_ORIGIN}/`
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "The Fracture — opening chapter",
          item: `${SITE_ORIGIN}/read`
        }
      ]
    },
    {
      "@type": "Article",
      headline: "The Fracture — opening chapter",
      about: { "@id": `${SITE_ORIGIN}/#book-1` },
      isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
      author: { "@id": `${SITE_ORIGIN}/#author` },
      inLanguage: "en",
      url: `${SITE_ORIGIN}/read`,
      description:
        "The free opening chapter of The Fracture, Book One of The Fractured Five by Tommy Green.",
      isAccessibleForFree: true
    }
  ]
};

const Block = ({ block, personnelEgg, onPersonnel }) => {
  if (block.t === "sep") {
    return <span className="reader-sep">• • •</span>;
  }
  if (block.t === "sys") {
    return <p className="reader-sys">{block.x}</p>;
  }
  if (personnelEgg && block.x && block.x.includes("Tylegn")) {
    const [before, after] = block.x.split("Tylegn");
    return (
      <p className={block.lead ? "lead" : undefined}>
        {before}
        <button
          type="button"
          onClick={onPersonnel}
          data-testid="reader-tylegn-egg"
          className="cursor-crosshair border-b border-dotted border-[hsl(32_80%_60%/0.6)] text-[hsl(32_80%_70%)] transition-colors duration-150 hover:border-solid hover:text-[hsl(32_92%_76%)]"
        >
          Tylegn
        </button>
        {after}
      </p>
    );
  }
  return <p className={block.lead ? "lead" : undefined}>{block.x}</p>;
};

export default function Read() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const { isEggActive, markEggFound, openOverlay } = useSecrets();
  const { code, kindleUrl, paperbackUrl, urlFor } = useStore();
  const bookTwo = BOOKS.find((b) => b.number === 2);
  const bookTwoUrl = bookTwo?.asin ? urlFor(bookTwo.asin) : SITE.bookTwoUrl;
  const personnelEgg = isEggActive("personnel");

  const openPersonnel = useCallback(() => {
    markEggFound("personnel");
    openOverlay({ kind: "personnel" });
  }, [markEggFound, openOverlay]);

  useSeo({
    title: `Read the opening chapter of ${EXCERPT.bookTitle} free | The Fractured Five`,
    description:
      "Read the full opening chapter of The Fracture, Book One of The Fractured Five by Tommy Green, free and with no signup. A colony dies, every instrument insists nothing is wrong, and 6.13 seconds are missing from the record.",
    path: "/read",
    type: "article",
    jsonLd: READ_JSONLD,
    jsonLdId: "read-jsonld"
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const pct = max <= 0 ? 0 : Math.min(100, (window.scrollY / max) * 100);
      setProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Anonymous read-depth telemetry: how far readers actually get through
  // the free excerpt. Milestones are reported once each per session.
  useEffect(() => {
    const sent = new Set();
    let prologueDone = false;

    const report = (depth, finished) => {
      recordReadProgress({ depth, prologueDone, finished }).catch(() => {});
    };

    const check = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const pct = max <= 0 ? 100 : Math.min(100, (window.scrollY / max) * 100);

      const chapter = document.querySelector('[data-part-boundary="1"]');
      if (!prologueDone && chapter) {
        const top = chapter.getBoundingClientRect().top;
        if (top < window.innerHeight * 0.5) {
          prologueDone = true;
          report(Math.round(pct), false);
        }
      }

      for (const milestone of [1, 25, 50, 75, 95]) {
        if (pct >= milestone && !sent.has(milestone)) {
          sent.add(milestone);
          report(milestone === 95 ? 100 : milestone, milestone === 95);
        }
      }
    };

    const timer = window.setTimeout(check, 1200);
    window.addEventListener("scroll", check, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", check);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <>
      <Atmosphere stars={false} />

      <div className="relative z-10 min-h-screen">
        <header className="sticky top-0 z-40 border-b border-border bg-[hsl(220_18%_6%/0.88)] backdrop-blur-md">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
            <Link
              to="/"
              data-testid="reader-close-button"
              className="inline-flex items-center gap-2 rounded-[10px] border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground transition-colors duration-150 hover:bg-white/5"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Close file
            </Link>

            <div className="hidden min-w-0 flex-1 px-4 text-center sm:block">
              <p className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {EXCERPT.seriesLine}
              </p>
            </div>

            <span
              data-testid="reader-progress"
              className="font-mono text-[11px] tabular-nums text-muted-foreground"
              aria-live="polite"
            >
              {Math.round(progress)}%
            </span>
          </div>

          <div className="h-[2px] w-full bg-[hsl(220_14%_14%)]">
            <div
              className="h-full bg-[hsl(32_92%_62%)]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 pb-4 pt-12 sm:px-6 sm:pt-16">
          <div className="mx-auto max-w-[68ch]">
            <p className="eyebrow">Excerpt · unedited</p>
            <h1 className="mt-3 text-4xl font-semibold leading-[1.02] sm:text-5xl">
              {EXCERPT.bookTitle}
            </h1>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(32_70%_66%)]">
              {EXCERPT.seriesLine}
            </p>
            <p className="mt-8 border-l-2 border-[hsl(32_92%_62%/0.5)] pl-4 text-base italic text-[hsl(210_14%_82%)]">
              {EXCERPT.epigraph}
            </p>
          </div>

          <article className="reader-prose mx-auto mt-14 max-w-[68ch]">
            {EXCERPT.parts.map((part, pi) => (
              <section
                key={part.id}
                data-part-boundary={pi}
                className={pi > 0 ? "mt-20" : undefined}
              >
                <div className="mb-9 border-t border-border pt-9">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {part.label}
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
                    {part.title}
                  </h2>
                </div>

                {part.blocks.map((block, bi) => (
                  <Block
                    key={`${part.id}-${bi}`}
                    block={block}
                    personnelEgg={personnelEgg}
                    onPersonnel={openPersonnel}
                  />
                ))}
              </section>
            ))}
          </article>

          {/* ---------- end gate ---------- */}
          <div className="mx-auto mt-16 max-w-[68ch]">
            <div className="panel-quiet px-5 py-6 text-center sm:px-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(2_80%_66%)]">
                Extract ends · remainder sealed
              </p>
              <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-[hsl(210_12%_78%)]">
                Vale keeps pulling. What she finds is not in this extract.
              </p>
            </div>

            <div className="paper mt-6 p-5 sm:p-7" data-testid="reader-end-gate">
              <div className="flex items-center justify-between gap-3 border-b border-[hsl(36_18%_74%)] pb-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(220_12%_38%)]">
                  Form SR-01 · signal registration
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(174_62%_28%)]">
                  Open
                </p>
              </div>

              <h2 className="mt-5 text-2xl font-semibold leading-tight text-[hsl(var(--ink))]">
                Want the rest?
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[hsl(220_14%_26%)]">
                <em>{EXCERPT.bookTitle}</em> is out now in ebook and
                paperback — the rest of the case is one tap away.
              </p>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <a
                  href={kindleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reader-buy-link"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-[hsl(220_18%_10%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(38_38%_94%)] transition-colors duration-150 hover:bg-[hsl(174_62%_26%)]"
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  {SITE.buyLabel}
                  <span aria-hidden="true" className="text-[hsl(38_20%_72%)]">
                    · {code}
                  </span>
                </a>

                <a
                  href={paperbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reader-paperback-link"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-[hsl(36_20%_70%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(220_18%_18%)] transition-colors duration-150 hover:bg-[hsl(36_24%_90%)]"
                >
                  <BookMarked className="h-3.5 w-3.5" />
                  Paperback
                </a>
              </div>

              <div className="mt-3">
                <StorePicker tone="ink" className="-ml-1.5" />
              </div>

              <p className="mt-6 border-t border-[hsl(36_16%_80%)] pt-5 text-[14.5px] leading-relaxed text-[hsl(220_14%_26%)]">
                Book Two, <em>The Weight of Blood</em>, is out now as well —{" "}
                <a
                  href={bookTwoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reader-book-two-link"
                  className="font-medium text-[hsl(174_62%_26%)] underline decoration-[hsl(174_62%_26%/0.4)] underline-offset-4 transition-colors duration-150 hover:text-[hsl(174_62%_18%)]"
                >
                  continue the case
                </a>
                . Four files still sealed; leave an address and you will be told
                the day each one unseals — next is 11 January 2027.
              </p>

              <div className="mt-5">
                <WaitlistForm
                  variant="paper"
                  source="reader"
                  idPrefix="reader"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <Link
                to="/#books"
                className="inline-flex items-center gap-2 rounded-[10px] border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground transition-colors duration-150 hover:bg-white/5"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to the series
              </Link>

              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center gap-2 rounded-[10px] border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors duration-150 hover:bg-white/5 hover:text-foreground"
              >
                <ArrowUp className="h-3.5 w-3.5" />
                Back to the top
              </button>
            </div>
          </div>
        </main>

        <div className="mt-16">
          <SiteFooter />
        </div>
      </div>
    </>
  );
}
